"use client";

import "react-phone-number-input/style.css";
import { Input } from "./ui/input";
import flags from "react-phone-number-input/flags";
import { SAFlag } from "./icons";
import ar from "react-phone-number-input/locale/ar";
import en from "react-phone-number-input/locale/en";
import dynamic from "next/dynamic";

const FormPhoneInput = dynamic(
  () => import("react-phone-number-input/react-hook-form"),
  { ssr: false } // Disable server-side rendering for this component
);

import { Control, useController } from "react-hook-form";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";
import { cn } from "@/lib/utils";
import ErrorAlert from "./error-alert";

type Props = {
  name: string;
  control?: Control<any>;
  disabled?: boolean;
};

const PhoneInput = ({ name, control, disabled }: Props) => {
  const locale = useLocale();
  const t = useTranslations();
  const [focused, setFocused] = useState(false);
  const { field, fieldState } = useController({ name, control });

  const props = {
    inputComponent: Input,
    flags: {
      ...flags,
      SA: SAFlag,
    },
    "aria-autocomplete": "none",
    autoComplete: "none",
    labels: locale === "ar" ? ar : en,
    disabled,
    required: true,
    className: cn(
      `[&_input]:text-base [&_input]:px-3 [&_input]:pt-4 [&_input]:pb-0 [&_.PhoneInputCountryIcon]:h-[unset] [&_.PhoneInputCountryIcon--border]:[box-shadow:unset] [&_.PhoneInputCountryIcon--border]:bg-[unset] flex-row-reverse gap-3 [&>div]:m-0 [&>div]:bg-white [&>div]:border [&>div]:rounded-2xl max-h-14 [&>div]:flex [&>div]:gap-2 [&>div]:flex-row-reverse [&>div]:px-2`,
      {
        "[&+span]:!text-[12px] [&+span]:font-light [&+span]:translate-x-0 [&+span]:-translate-y-12":
          field.value || field.value === 0 || focused,
        "[&_input]:bg-[#ececec] [&_.PhoneInputCountry]:bg-[#ececec]": disabled,
        "[&_input]:border-error [&_input]:!ring-error": fieldState.invalid,
      }
    ),
  };

  return (
    <label className={"relative block"}>
      <FormPhoneInput
        name={name}
        control={control}
        defaultCountry={"SA"}
        {...props}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        limitMaxLength
        numberInputProps={{
          dir: "ltr",
        }}
      />
      <span
        className={cn(
          "transition-all pointer-events-none select-none text-sm text-gray-dark absolute ltr:left-3 rtl:right-3 -translate-y-10",
          {
            "text-muted": field.value || focused,
          }
        )}
      >
        {t("phoneInput.placeholder")} <span className="text-red-500">*</span>
      </span>
      <ErrorAlert title={fieldState?.error?.message} />
    </label>
  );
};

export default PhoneInput;
